import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Axios from 'axios';
import csrfToken from '../shared/adminCsrf';
import { AIPolicySnapshot, UsageReport, UserOption } from './types';

const QUERY_KEY = ['AIPolicy'];

const headers = () => ({ 'X-CSRF-Token': csrfToken() });

// The gateway's own error text is the useful part — a 502 here means it was unreachable, answered
// with something that was not a config, or refused the write outright.
const errorFrom = (error: unknown) => {
  const message = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
  return message || 'The gateway did not accept that change.';
};

export const useAIPolicy = () => useQuery({
  queryFn: async () => (await Axios.get<AIPolicySnapshot>('/admin/ai_policy')).data,
  queryKey: QUERY_KEY,
  retry: false,
});

export interface SetUserLimitInput {
  daily_limit_usd?: string;
  identity: string;
  requests_per_minute?: string;
}

// Mutations answer with the gateway's own snapshot, so the cache is seeded rather than invalidated.
export const useAIPolicyMutations = () => {
  const queryClient = useQueryClient();
  const onSuccess = (data: AIPolicySnapshot) => queryClient.setQueryData(QUERY_KEY, data);

  const updateDefaults = useMutation({
    mutationFn: async (vars: { daily_limit_usd: string; requests_per_minute: string }) => (
      (await Axios.put<AIPolicySnapshot>('/admin/ai_policy/update_defaults', vars, { headers: headers() })).data
    ),
    onSuccess,
  });

  const setUserLimit = useMutation({
    mutationFn: async (vars: SetUserLimitInput) => (
      (await Axios.post<AIPolicySnapshot>('/admin/ai_policy/set_user_limit', vars, { headers: headers() })).data
    ),
    onSuccess,
  });

  const clearUserLimit = useMutation({
    mutationFn: async (identity: string) => (
      (await Axios.delete<AIPolicySnapshot>('/admin/ai_policy/clear_user_limit', {
        data: { identity },
        headers: headers(),
      })).data
    ),
    onSuccess,
  });

  return { clearUserLimit, errorFrom, setUserLimit, updateDefaults };
};

export const useAIUsage = (days: number) => useQuery({
  queryFn: async () => (await Axios.get<UsageReport>('/admin/ai_policy/usage', { params: { days } })).data,
  queryKey: ['AIPolicyUsage', days],
  retry: false,
});

// Driven by the select's own input rather than AsyncSelect: SPASelect wraps the plain react-select,
// and keying the query on the term gets caching and in-flight de-duping for free.
export const useUserSearch = (term: string) => useQuery({
  enabled: term.trim().length >= 2,
  keepPreviousData: true,
  queryFn: async () => (
    (await Axios.get<{ data: UserOption[] }>('/admin/ai_policy/search_users', { params: { q: term } })).data.data
  ),
  queryKey: ['AIPolicyUserSearch', term.trim()],
  retry: false,
});
